import { BlockParameters, SIM_SUN4D, SOLAR_HEAT_GAIN_STRATEGIES, UnobstructedMode } from '@visualizer/types';
import { AddMetadata, BlockKeys } from '../BlockCatalog';
import { StageContext } from '../Context';
import { PipelineBlock } from '../PipelineBlock';
import { PipelineStage, StageExecutionMode } from '../Stage';
import { computeTotalEnergyFromSensors } from 'Utils/EnergyUtils';
import IndexThresholdStage from '../stages/filters/IndexThreshold';
import UnobstructedFilterStage from '../stages/filters/UnobstructedFilterStage';
import AverageStage from '../stages/preprocess/AverageStage';
import SortStage from '../stages/preprocess/SortStage';
import ProjectStage, { ProjectionType } from '../stages/transformations/ProjectStage';

type ShgStageType = 'average' | 'unobstructed' | 'projection' | 'sort' | 'threshold';
export type ShgPotentialsStrategy = typeof SOLAR_HEAT_GAIN_STRATEGIES.MATCH_HEATING_PEAKS;

export type ShgPotentialsBlockParameters = BlockParameters & {
    strategy?: ShgPotentialsStrategy;
    demand: number;
    gValue?: number;
    tolerance?: number;
    maxIterations?: number;
    frame?: number;
};

@AddMetadata({
    type: BlockKeys.ShgPotentials,
    name: 'Solar Heat Gains',
    description: 'Smartly filters SPs to match heating demand',
    targetLayer: {
        simKind: SIM_SUN4D,
        frameCount: 12,
        frame: 'heatingPeaks',
    },
})
class ShgPotentialsBlock extends PipelineBlock<ShgPotentialsBlockParameters> {
    // Declare --> map is populated in parent class and doesn't need to be re-initialized empty here:
    declare private stageByType: Map<ShgStageType, PipelineStage<any>> | undefined;

    constructor(params: ShgPotentialsBlockParameters, stringId?: string) {
        super(params, stringId);
    }

    protected override defineStages() {
        const stageByType = this.getStageByType();
        stageByType.clear();

        const averageStage = new AverageStage();
        const unobstructedStage = new UnobstructedFilterStage({ unobstructedMode: UnobstructedMode.Corners, isValue: true });
        const projectionStage = new ProjectStage({ projectionType: ProjectionType.HORIZONTAL });
        const sortStage = new SortStage();
        const thresholdStage = new IndexThresholdStage({
            thresholdRange: [0, 100],
            tolerance: 0,
            subSortMode: 0,
            executionMode: StageExecutionMode.BY_FLOOR,
        });

        this.getStageByType().set('average', averageStage);
        this.getStageByType().set('unobstructed', unobstructedStage);
        this.getStageByType().set('projection', projectionStage);
        this.getStageByType().set('sort', sortStage);
        this.getStageByType().set('threshold', thresholdStage);

        return [averageStage, unobstructedStage, projectionStage, sortStage, thresholdStage];
    }

    protected override executeStages(_stages: PipelineStage<any>[], context: StageContext): StageContext {
        switch (this.getParams().strategy) {
            case SOLAR_HEAT_GAIN_STRATEGIES.MATCH_HEATING_PEAKS:
            default:
                return this.executeMatchHeatingDemand(context);
        }
    }

    private executeStageByKey(key: ShgStageType, context: StageContext): StageContext {
        const stage = this.getStageByType().get(key);
        if (!stage) {
            throw new Error(`ShgPotentialsBlock: Stage "${key}" is not registered.`);
        }
        const nextContext = stage.execute(context);

        return nextContext;
    }

    private getStageByType(): Map<ShgStageType, PipelineStage<any>> {
        if (!this.stageByType) this.stageByType = new Map<ShgStageType, PipelineStage<any>>();
        return this.stageByType;
    }

    /**
     * 1st Strategy: Match Heating Peak Demand
     *
     * Updates the threshold value to have the highest value SensorPoints matching the
     * demand in input (in this case a Monthly Peak).
     *
     * @returns Filtered Sensor Points that match the given peak energy demand.
     */
    private executeMatchHeatingDemand(context: StageContext): StageContext {
        const averagedContext = this.executeStageByKey('average', context);
        const projectedContext = this.executeStageByKey('projection', averagedContext);
        const unobstructedContext = this.executeStageByKey('unobstructed', projectedContext);
        const sortedContext = this.executeStageByKey('sort', unobstructedContext);

        const thresholdStage = this.getStageByType().get('threshold');
        if (!thresholdStage) {
            throw new Error('ShgPotentialsBlock: Stage "threshold" is not registered.');
        }

        const demand = this.getParams().demand;
        const maxIterations = this.getParams().maxIterations ?? 20;
        const tolerance = this.getParams().tolerance ?? 1000;

        if (demand <= 0 || maxIterations <= 0) {
            const nextContext = thresholdStage.execute(sortedContext, {
                thresholdRange: [0, 100],
                tolerance: 0,
                subSortMode: 0,
                executionMode: StageExecutionMode.BY_FLOOR,
            } as never);

            return nextContext;
        }

        let low = 0;
        let high = 100;
        let lastValidMid = low;

        for (let i = 0; i < maxIterations; i++) {
            const mid = (low + high) / 2;
            const thresholdedContext = thresholdStage.execute(sortedContext, {
                thresholdRange: [mid, 100],
                tolerance: 0,
                subSortMode: 0,
                executionMode: StageExecutionMode.BY_FLOOR,
            } as never);
            const transmittedEnergy = this.computeTransmittedEnergy(thresholdedContext);

            if (transmittedEnergy >= demand && Math.abs(transmittedEnergy - demand) <= tolerance) {
                lastValidMid = mid;
                break;
            }

            if (transmittedEnergy >= demand) {
                lastValidMid = mid;
                low = mid;
            } else {
                high = mid;
            }
        }

        const finalContext = thresholdStage.execute(sortedContext, {
            thresholdRange: [lastValidMid, 100],
            tolerance: 0,
            subSortMode: 0,
            executionMode: StageExecutionMode.BY_FLOOR,
        } as never);

        return finalContext;
    }

    private computeTransmittedEnergy(context: StageContext): number {
        const sensorsValue = computeTotalEnergyFromSensors(context.sensors);
        const gValue = (this.getParams().gValue ?? 100) / 100;
        return sensorsValue * gValue;
    }
}

export default ShgPotentialsBlock;
